import React, { useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import useUsers from 'api/useUsers';
import { EFeatureFlag } from 'constants/enums';

import { Form } from 'components/ui';

import usePortalSettings from '../../api/portal/usePortalSettings';
import PortalHeader from '../../components/PortalHeader/PortalHeader';
import { Tab, Tabs, TabsList } from '../../components/Tabs/Tabs';
import { TabsContent } from '../../components/ui/tabs';
import { EToasterStatus, showToast } from '../../utils/showToast';

import { buildParsedSettingsData, SETTINGS_FORM_SCHEMA } from './constants';
import DesignLayoutSettings from './DesignLayoutSettings';
import LoginPopupSettings from './LoginPopupSettings';
import PortalSettings from './PortalSettings';
import { getInitialFormValues } from './PortalSettings/utils';
import { SettingsFormData } from './types';

enum EPortalSettingsTabs {
    PORTAL_THEME = 'portal-theme',
    LOGIN_MODAL = 'login-modal',
    PORTAL_SETTINGS = 'portal-settings'
}

const PORTAL_SETTINGS_TABS: Tab[] = [
    { id: EPortalSettingsTabs.PORTAL_THEME, value: 'Portal Theme', testId: 'PortalSettings-Tab-Theme' },
    { id: EPortalSettingsTabs.LOGIN_MODAL, value: 'Login Modal', testId: 'PortalSettings-Tab-LoginModal' },
    { id: EPortalSettingsTabs.PORTAL_SETTINGS, value: 'Portal Settings', testId: 'PortalSettings-Tab-PortalSettings' }
];

const BuilderView = () => {
    const { getPortalSettingsData, editPortalSettings } = usePortalSettings();
    const { fetchFeatureFlags } = useUsers({});
    const showLoginBtnFlag = fetchFeatureFlags.data?.featureFlags?.[EFeatureFlag.DASHBOARD_SHOW_LOGIN_BUTTON_PORTAL_STUDIO];

    const initialValues = useMemo(
        () => getInitialFormValues(getPortalSettingsData?.data ?? {}, showLoginBtnFlag),
        [getPortalSettingsData?.data, showLoginBtnFlag]
    );

    const settingsFormConfig = useForm<SettingsFormData>({
        resolver: zodResolver(SETTINGS_FORM_SCHEMA),
        defaultValues: initialValues,
        mode: 'onChange',
        shouldFocusError: true
    });

    const {
        control,
        watch,
        handleSubmit,
        reset,
        formState: { isValid, isDirty, errors },
    } = settingsFormConfig;

    const navigate = useNavigate();
    const location = useLocation();

    const pathSegments = location.pathname.split('/').filter(Boolean);
    const currentTab = Object.values(EPortalSettingsTabs).includes(pathSegments[pathSegments.length - 1] as EPortalSettingsTabs)
        ? (pathSegments[pathSegments.length - 1] as EPortalSettingsTabs)
        : EPortalSettingsTabs.PORTAL_THEME;


    useEffect(() => {
        reset(initialValues);
    }, [initialValues, reset]);

    useEffect(() => {
        const lastSegment = pathSegments[pathSegments.length - 1];
        if (!Object.values(EPortalSettingsTabs).includes(lastSegment as EPortalSettingsTabs)) {
            navigate(EPortalSettingsTabs.PORTAL_THEME, { replace: true });
        }
    }, [navigate, pathSegments]);

    const onSettingsFormSubmit = useCallback(
        async (data: SettingsFormData) => {
            const parsedSettingsData = buildParsedSettingsData(data);

            try {
                await editPortalSettings.mutateAsync(parsedSettingsData);
                await getPortalSettingsData.refetch();
                showToast({
                    message: 'Portal settings were successfully updated',
                    status: EToasterStatus.SUCCESS
                });
            } catch (error: any) {
                showToast({
                    message:
                        error?.response?.data?.message ||
                        'Portal settings were not updated. Please try again',
                    status: EToasterStatus.ERROR
                });
            }
        },
        [editPortalSettings, getPortalSettingsData]
    );

    return (
        <div className="overflow-scroll">
            <Form {...settingsFormConfig}>
                <form onSubmit={handleSubmit(onSettingsFormSubmit)}>
                    <PortalHeader
                        title="Builder"
                        primaryButtonText="Save"
                        secondaryButtonVariant="outline"
                        secondaryButtonIcon={<FontAwesomeIcon icon="fa-regular fa-eye" />}
                        primaryButtonState={{ disabled: !isValid || !isDirty }}
                    />
                    <div className="w-full px-7 pb-20">
                        <Tabs value={currentTab} onValueChange={navigate}>
                            <TabsList tabs={PORTAL_SETTINGS_TABS} />
                            <TabsContent value={EPortalSettingsTabs.PORTAL_THEME} className="mt-5">
                                <DesignLayoutSettings
                                    control={control}
                                    errors={errors}
                                    hasFeatureFlagShowLoginButton={showLoginBtnFlag}
                                />
                            </TabsContent>
                            <TabsContent value={EPortalSettingsTabs.LOGIN_MODAL} className="mt-5">
                                <LoginPopupSettings
                                    control={control}
                                    errors={errors}
                                    watch={watch}
                                    initialFontSize={initialValues.loginPopupFontSize}
                                />
                            </TabsContent>
                            <TabsContent value={EPortalSettingsTabs.PORTAL_SETTINGS} className="mt-5">
                                <PortalSettings control={control} errors={errors} watch={watch} />
                            </TabsContent>
                        </Tabs>
                    </div>
                </form>
            </Form>
        </div>
    );
};

export default BuilderView;
